import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  BookOpen,
  MessageSquare,
  Package,
  ShoppingBag,
  Ticket,
  CalendarClock,
  MapPin,
  LogOut,
  Menu,
  X,
  Settings,
  Star,
  Newspaper,
  Mail,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { authFetch } from "../../lib/api";
import { useBrandName, useBrandShort, useSettings } from "../../context/SettingsContext";
import { Button } from "../ui/button";
import { BTN_OUTLINE, mediaSrc } from "../../config/brand";

const navItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { path: "/admin/offers", label: "Offres & Cursus", icon: Package },
  { path: "/admin/products", label: "Produits boutique", icon: ShoppingBag },
  { path: "/admin/promos", label: "Codes promo", icon: Ticket },
  { path: "/admin/slots", label: "Slots & Dispo", icon: CalendarClock },
  { path: "/admin/centers", label: "Centres", icon: MapPin },
  { path: "/admin/stages", label: "Stages", icon: Calendar },
  { path: "/admin/bookings", label: "Réservations", icon: BookOpen },
  { path: "/admin/contacts", label: "Contacts", icon: MessageSquare },
  { path: "/admin/emails", label: "E-mails", icon: Mail },
  { path: "/admin/testimonials", label: "Avis", icon: Star },
  { path: "/admin/blog", label: "Blog", icon: Newspaper },
  { path: "/admin/settings", label: "Paramètres", icon: Settings },
];

function BrandMark({ brandName, brandShort, logoUrl }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      {logoUrl ? (
        <img
          src={mediaSrc(logoUrl)}
          alt=""
          className="h-8 w-8 rounded-[0.45rem] object-contain bg-white border border-slate-200 p-0.5 shrink-0"
        />
      ) : (
        <span className="admin-brand-mark shrink-0" aria-hidden="true">
          {brandShort}
        </span>
      )}
      <div className="min-w-0">
        <p className="text-[0.9375rem] font-semibold text-slate-900 truncate leading-tight">{brandName}</p>
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400 mt-0.5">Admin</p>
      </div>
    </div>
  );
}

function NavBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-[#3a00e0] text-white text-[10px] font-semibold tabular-nums flex items-center justify-center">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function NavLinks({ isActive, onNavigate, badges = {} }) {
  return navItems.map(({ path, label, icon: Icon, exact }) => (
    <Link
      key={path}
      to={path}
      onClick={onNavigate}
      className={`admin-nav-link ${isActive(path, exact) ? "is-active" : ""}`}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-80" />
      {label}
      <NavBadge count={badges[path]} />
    </Link>
  ));
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const brandName = useBrandName();
  const brandShort = useBrandShort();
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [inboxBadges, setInboxBadges] = React.useState({});

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const currentPage = navItems.find(({ path, exact }) => isActive(path, exact))?.label || "Admin";

  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    let cancelled = false;
    authFetch("/api/admin/stats")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const bookings = Number(data.unread_bookings) || 0;
        const contacts = Number(data.unread_contacts) || 0;
        setInboxBadges({
          "/admin": bookings + contacts,
          "/admin/bookings": bookings,
          "/admin/contacts": contacts,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  React.useEffect(() => {
    document.body.classList.add("admin-root");
    return () => document.body.classList.remove("admin-root");
  }, []);

  React.useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const closeSidebar = () => setSidebarOpen(false);

  const sidebarInner = (
    <>
      <div className="px-4 py-5 border-b border-slate-200">
        <BrandMark brandName={brandName} brandShort={brandShort} logoUrl={settings?.logo_url} />
      </div>
      <nav className="flex-1 px-2.5 py-3 space-y-0.5 overflow-y-auto">
        <NavLinks isActive={isActive} onNavigate={closeSidebar} badges={inboxBadges} />
      </nav>
      <div className="p-3 border-t border-slate-200">
        <p className="text-xs text-slate-500 truncate px-1 mb-2" title={user?.email}>
          {user?.email}
        </p>
        <Button
          variant="outline"
          size="sm"
          className={`w-full ${BTN_OUTLINE}`}
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Déconnexion
        </Button>
      </div>
    </>
  );

  return (
    <div className="admin-root min-h-screen flex">
      <aside className="hidden md:flex w-[15.5rem] flex-col border-r border-slate-200 bg-white shrink-0">
        {sidebarInner}
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeSidebar}
            aria-hidden="true"
          />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white border-r border-slate-200 flex flex-col shadow-xl animate-in slide-in-from-left duration-200">
            <div className="px-4 py-4 flex justify-between items-center border-b border-slate-200">
              <BrandMark brandName={brandName} brandShort={brandShort} logoUrl={settings?.logo_url} />
              <button
                onClick={closeSidebar}
                className="p-2 rounded-md hover:bg-slate-100 transition-colors text-slate-600"
                aria-label="Fermer le menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 px-2.5 py-3 space-y-0.5 overflow-y-auto">
              <NavLinks isActive={isActive} onNavigate={closeSidebar} badges={inboxBadges} />
            </nav>
            <div className="p-3 border-t border-slate-200">
              <p className="text-xs text-slate-500 truncate px-1 mb-2">{user?.email}</p>
              <Button
                variant="outline"
                size="sm"
                className={`w-full ${BTN_OUTLINE}`}
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 bg-slate-50">
        <header className="sticky top-0 z-40 flex items-center justify-between h-14 px-4 md:px-6 border-b border-slate-200 bg-white/90 backdrop-blur-md">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-md hover:bg-slate-100 transition-colors text-slate-700"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-slate-900 truncate">{currentPage}</p>
              <p className="hidden sm:block text-[11px] text-slate-400 truncate">Back-office {brandName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden lg:inline text-xs text-slate-500 truncate max-w-[14rem]" title={user?.email}>
              {user?.email}
            </span>
            <Button
              variant="outline"
              size="sm"
              className={`${BTN_OUTLINE} h-8 px-3`}
              onClick={handleLogout}
              aria-label="Déconnexion"
            >
              <LogOut className="h-3.5 w-3.5 md:mr-1.5" />
              <span className="hidden md:inline">Quitter</span>
            </Button>
          </div>
        </header>
        <main className="flex-1 px-4 py-5 md:px-6 md:py-7 lg:px-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
